// Direct sign-in links for the GitLab backend, e.g.
//
//   https://org.fireweaver.org/gitlab/org/ml
//   https://org.fireweaver.org/gitlab/org/org?open=/file/inbox.org
//   https://org.fireweaver.org/sign_in?gitlab=org/ml        (older form)
//
// src/index.js handles these before the app renders, so neither organice's
// sign-in page nor its landing page is shown: the link goes straight to
// GitLab's authorization request (or, already signed in to that project,
// straight to the file), and once GitLab redirects back with a grant the
// app lands on `open` (default: DEFAULT_POST_SIGN_IN_PATH).

import { persistField, getPersistedField, clearRepositoryCache } from '../util/settings_persister';
import createGitLabSyncBackendClient, {
  createGitlabOAuth,
  gitLabProjectIdFromURL,
} from '../sync_backend_clients/gitlab_sync_backend_client';

export const GITLAB_BASE_URL = 'https://www.fireweaver.org';
export const DEFAULT_POST_SIGN_IN_PATH = '/file/roam/gtd.org';
const DIRECT_LINK_PREFIX = '/gitlab/';

// Only same-origin absolute paths: `//evil.example` or `https://...` would
// turn the link into an open redirect.
const safeOpenPath = (openPath) =>
  openPath && openPath.startsWith('/') && !openPath.startsWith('//')
    ? openPath
    : DEFAULT_POST_SIGN_IN_PATH;

const projectURLFrom = (project) =>
  project.includes('://') ? project : `${GITLAB_BASE_URL}/${project.replace(/^\/+/, '')}`;

// `project` is a path under GITLAB_BASE_URL, e.g. `org/ml`.
export const directSignInLink = (project, openPath) =>
  DIRECT_LINK_PREFIX +
  project.replace(/^\/+|\/+$/g, '') +
  (openPath ? `?open=${encodeURIComponent(openPath)}` : '');

// The project a direct link names, or null when `location` is not one.
const directLinkProject = (location) => {
  if (location.pathname.startsWith(DIRECT_LINK_PREFIX)) {
    return decodeURIComponent(location.pathname.slice(DIRECT_LINK_PREFIX.length)).replace(
      /\/+$/,
      ''
    );
  }
  if (location.pathname === '/sign_in') {
    return new URLSearchParams(location.search).get('gitlab');
  }
  return null;
};

const unsyncedFilePaths = () => {
  try {
    const isDirty = JSON.parse(localStorage.getItem('isDirty')) || {};
    return Object.keys(isDirty).filter((path) => isDirty[path]);
  } catch {
    return [];
  }
};

export const signInToGitLabProject = (projectURL, postSignInPath) => {
  const projectId = gitLabProjectIdFromURL(projectURL);
  if (!projectId) {
    alert('Project does not appear to be a valid gitlab.com URL');
    return false;
  }
  persistField('authenticatedSyncService', 'GitLab');
  persistField('gitLabProject', projectId);
  persistField('postSignInPath', postSignInPath || null);
  createGitlabOAuth().fetchAuthorizationCode();
  return true;
};

// Where to go once a GitLab sign-in has completed; consumed on read so a
// later, ordinary sign-in lands on `/` again.
export const takePostSignInPath = () => {
  const path = getPersistedField('postSignInPath', true);
  persistField('postSignInPath', null);
  return path ? safeOpenPath(path) : null;
};

// Returns true when `location` is a direct link and the page is now
// navigating away (to GitLab, or to the target file). The caller must then
// not render the app.
export const beginDirectGitLabSignIn = (location = window.location) => {
  const project = directLinkProject(location);
  if (!project) return false;

  const projectURL = projectURLFrom(project);
  const projectId = gitLabProjectIdFromURL(projectURL);
  if (!projectId) return false;
  const openPath = safeOpenPath(new URLSearchParams(location.search).get('open'));

  const currentService = getPersistedField('authenticatedSyncService', true);
  const currentProject = getPersistedField('gitLabProject', true);
  if (currentService === 'GitLab' && currentProject === projectId) {
    if (createGitlabOAuth().isAuthorized()) {
      location.replace(location.origin + openPath);
      return true;
    }
  } else if (currentService) {
    const current =
      currentService === 'GitLab' ? decodeURIComponent(currentProject || '') : currentService;
    const target = decodeURIComponent(projectId);
    const unsynced = unsyncedFilePaths();
    const warning = unsynced.length
      ? `These files have changes that are not synced yet and will be lost:\n` +
        unsynced.map((path) => `  ${path}`).join('\n')
      : 'All files are synced.';
    if (
      !window.confirm(
        `You are signed in to ${current}. Sign out and sign in to ${target} instead?\n\n` + warning
      )
    ) {
      location.replace(location.origin + '/');
      return true;
    }
    // The cached files and settings belong to the repository being left; see
    // guardRepositoryCache(), which would also catch this on the next load.
    clearRepositoryCache();
  }

  // Drop any token from an earlier sign-in so the fresh grant is the one used.
  createGitlabOAuth().reset();
  return signInToGitLabProject(projectURL, openPath);
};

// True when GitLab has just redirected back with a grant for a sign-in that a
// direct link started -- the case src/index.js completes without the app.
export const isCompletingDirectSignIn = (location = window.location) =>
  getPersistedField('authenticatedSyncService', true) === 'GitLab' &&
  !!getPersistedField('postSignInPath', true) &&
  new URLSearchParams(location.search).has('code');

// Forget a sign-in that did not complete, so the next page load starts clean
// instead of trying to redeem the same, already used, grant again.
export const abandonGitLabSignIn = () => {
  persistField('authenticatedSyncService', null);
  persistField('gitLabProject', null);
  persistField('postSignInPath', null);
  createGitlabOAuth().reset();
};

// Redeems the grant GitLab redirected back with, checks the project is
// reachable, and navigates on: to the direct link's target if one is pending,
// otherwise to `/`. Resolves false (after telling the user why) on failure.
export const completeGitLabSignIn = async (oauthClient) => {
  let success = false;
  try {
    success = await oauthClient.isReturningFromAuthServer();
    await oauthClient.getAccessToken();
  } catch {
    success = false;
  }
  if (!success) {
    // Edge case: somehow OAuth success redirect occurred but there isn't a code in
    // the current location's search params. This /shouldn't/ happen in practice.
    alert('Unexpected sign in error, please try again');
    return false;
  }

  const syncClient = createGitLabSyncBackendClient(oauthClient);
  let isAccessible = false;
  try {
    isAccessible = await syncClient.isProjectAccessible();
  } catch (e) {
    // This used to sit outside any try/catch, so a throw here became an
    // unhandled rejection: the user was dropped back on the landing page with
    // no alert, no log and no way to tell sign-in had even been attempted.
    console.error('Error checking whether the GitLab project is accessible:', e);
    alert(`Failed to check GitLab project access: ${e.message}`);
    return false;
  }
  if (!isAccessible) {
    alert('Failed to access GitLab project - is the URL correct?');
    return false;
  }

  const postSignInPath = takePostSignInPath();
  if (postSignInPath) {
    window.location.replace(window.location.origin + postSignInPath);
  } else {
    window.location.search = '';
  }
  return true;
};
